import { acceptHMRUpdate, defineStore } from 'pinia'
import api from '@api'
import type {
  AutocompleteRequest,
  ContactByHash,
  CarrierCreateFirstStep,
  Carriers,
  CarriersAllListParams,
  CarriersListParams,
  LanguageEnum,
  TransportationModeEnum,
} from '@apiTypes'
import { defaultLanguage } from '@/plugins/i18n'

interface CarriersState {
  currentCarrierByHash: ContactByHash | null
}

export const useCarriersStore = defineStore('carriers', {
  state: (): CarriersState => ({
    currentCarrierByHash: null,
  }),
  getters: {
    getCurrentLanguage: state => {
      return state.currentCarrierByHash?.preferredLanguage || defaultLanguage
    },
  },
  actions: {
    setCurrentCarrier(carrier: ContactByHash) {
      this.currentCarrierByHash = carrier
    },
    fetchCarriers(query: CarriersListParams) {
      return api.carriers.carriersList(query)
    },
    fetchCarrier(id: number) {
      return api.carriers.carriersRetrieve({ id })
    },
    fetchCarrierContacts(id: number, transportationMode?: TransportationModeEnum) {
      return api.carriers.carriersContactsList({ id, transportationMode })
    },
    autocomplete(data: AutocompleteRequest) {
      return api.carriers.carriersAutocompleteCreate(data)
    },
    fetchAllCarriers(query: CarriersAllListParams) {
      return api.carriers.carriersAllList(query)
    },
    addCarrier(data: Carriers.CarriersCreate.RequestBody) {
      return api.carriers.carriersCreate(data)
    },
    updateCarrier(id: number, params: Carriers.CarriersPartialUpdate.RequestBody) {
      return api.carriers.carriersPartialUpdate({ id }, params)
    },
    validateFirstStep(data: CarrierCreateFirstStep) {
      return api.carriers.carriersValidateFirstStepCreate(data)
    },
    deleteCarrier(id: number) {
      return api.carriers.carriersDestroy({ id })
    },
    setActive(id: number, isActive: boolean) {
      return api.carriers.carriersIsActivePartialUpdate({ id }, { isActive })
    },
    fetchCurrentCarrierByHash(hash: string) {
      return api.byHash.byHashContactsRetrieve({ hash }, { skipAuthToken: true }).then(response => {
        this.setCurrentCarrier(response.data)

        return response
      })
    },
    updateCarrierByHashLanguage(hash: string, preferredLanguage: LanguageEnum) {
      return api.byHash.byHashContactsPartialUpdate({ hash }, { preferredLanguage }, {
        skipAuthToken: true,
        skipAuthRefresh: true,
      }).then(result => {
        this.setCurrentCarrier(result.data)

        return result.data
      })
    },
    downloadCarrierByHashTransportOrderDocument(contactHash: string, hash: string) {
      return api.byHash.byHashContactsAuctionsDownloadTransportOrderRetrieve({ contactHash, hash }, { skipAuthToken: true, format: 'blob' })
    },
    resetCurrentCarrier(): void {
      this.currentCarrierByHash = null
    },
  },
})

if (import.meta.hot)
  import.meta.hot.accept(acceptHMRUpdate(useCarriersStore, import.meta.hot))
